import React, { useState } from "react";

import { Panel, Th } from "./Panel";
import StatusIcons from "../../StatusIcons/StatusIcons";
import ShipmentHistoryModal from "../../ShipmentHistoryModal/ShipmentHistoryModal";
import Tooltip from "../../Tooltip/Tooltip";
import HyperLink from "../../HyperLink/HyperLink";
import { MdOutlineContentCopy, MdOutlineHistory } from "react-icons/md";
import { FcCheckmark } from "react-icons/fc";
import { useTranslation } from "react-i18next";

/**
 * Packages Detail. Mirrors ShipmentDetails.jsx — same columns, same
 * conditional columns, same unit suffixes, same per-parcel history modal.
 *
 * Note the carrier-tracking column reads c_p_shipment_details[index], i.e. it
 * pairs by array position with shipment_details rather than by parcel id.
 * That is how production does it; changing it here would make the two screens
 * disagree.
 */
const PackagesPanel = ({ data }) => {
  const { t } = useTranslation("admin");
  const [copied, setCopied] = useState("");
  const [historyModal, setHistoryModal] = useState(false);

  const unit = data?.unit === "Imperial";
  const dimUnit = unit ? "in" : "cm";
  const weightUnit = unit ? "lb" : "kg";
  const volumeUnit = unit ? "cbm" : "cbf";

  const parcels = data?.shipment_details || [];
  const carrierParcels = data?.c_p_shipment_details || [];
  const hasCarrierTracking = carrierParcels.length > 0;
  const perPackageInsurance =
    data?.insurance && data?.insurance_type === "Package";

  const copy = (values, key) => {
    navigator.clipboard
      ?.writeText(values.join(",\n"))
      .then(() => {
        setCopied(key);
        setTimeout(() => setCopied(""), 2000);
      })
      .catch((error) => console.error("Failed to copy to clipboard: " + error));
  };

  const CopyButton = ({ label, values, copyKey }) => (
    <button
      onClick={() => copy(values, copyKey)}
      className="flex items-center gap-1 rounded border border-ui-border bg-ui-surface px-1.5 py-0.5 font-manrope text-[10px] font-semibold text-ui-text-muted hover:bg-ui-surface-sunken"
    >
      {copied === copyKey ? (
        <FcCheckmark size={12} />
      ) : (
        <MdOutlineContentCopy size={12} />
      )}
      {label}
    </button>
  );

  return (
    <>
      <ShipmentHistoryModal open={historyModal} setOpen={setHistoryModal} />
      <Panel
        title={t("invoice.packages")}
        chip={t("invoice.parcelCount", { count: parcels.length })}
        hint={t("invoice.asDeclaredAtBooking")}
        actions={
          <div className="flex items-center gap-1.5">
            {parcels.length > 0 && (
              <CopyButton
                label={t("invoice.parcelIDs")}
                copyKey="pid"
                values={parcels.map((p) => p?.parcel_id).filter(Boolean)}
              />
            )}
            {hasCarrierTracking && (
              <CopyButton
                label={t("invoice.trackingIDs")}
                copyKey="sid"
                values={carrierParcels
                  .map((p) => p?.shipment_tracking_id)
                  .filter(Boolean)}
              />
            )}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse tabular-nums">
            <thead>
              <tr className="border-b border-ui-border-strong bg-ui-surface-sunken">
                <Th align="center">#</Th>
                <Th>{t("invoice.description")}</Th>
                <Th>{t("invoice.parcelId")}</Th>
                <Th>{t("invoice.parcelStatus")}</Th>
                {hasCarrierTracking && <Th>{t("invoice.carrierTrackingColumn")}</Th>}
                {perPackageInsurance && <Th align="right">{t("invoice.insurance")}</Th>}
                <Th align="right">{t("invoice.dimensions")}</Th>
                <Th align="right">{t("invoice.weight")}</Th>
                <Th align="right">{t("invoice.volume")}</Th>
              </tr>
            </thead>
            <tbody className="font-manrope text-[11.5px] font-medium text-ui-text">
              {parcels.map((item, index) => (
                <tr
                  key={item?.id ?? index}
                  className="border-b border-ui-border-subtle hover:bg-ui-row-hover"
                >
                  <td className="px-[9px] py-1 text-center text-ui-text-muted">
                    {index + 1}
                  </td>
                  <td className="px-[9px] py-1 text-ui-text-secondary">
                    {item?.description ?? "-"}
                  </td>
                  <td className="px-[9px] py-1">
                    <HyperLink code={item?.parcel_id} />
                  </td>
                  <td className="px-[9px] py-1">
                    <div className="flex items-center gap-1">
                      <StatusIcons status={item?.tracking_status ?? "N/A"} showTitle />
                      {item?.parcel_id && item?.tracking_status && (
                        <Tooltip title={t("invoice.clickToViewHistory")}>
                          <div>
                            <MdOutlineHistory
                              size={15}
                              className="parcel__history_icone"
                              onClick={() => setHistoryModal(item.id)}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  {hasCarrierTracking && (
                    <td className="px-[9px] py-1 text-ui-text-secondary">
                      {carrierParcels[index]?.shipment_tracking_id ?? "-"}
                    </td>
                  )}
                  {perPackageInsurance && (
                    <td className="px-[9px] py-1 text-right text-ui-text-secondary">
                      {item?.insurance ?? "-"}
                    </td>
                  )}
                  <td className="px-[9px] py-1 text-right text-ui-text-secondary">
                    {item?.length ?? "0"} x {item?.width ?? "0"} x{" "}
                    {item?.height ?? "0"} ({dimUnit})
                  </td>
                  <td className="px-[9px] py-1 text-right">
                    {item?.weight ?? "0"} ({weightUnit})
                  </td>
                  <td className="px-[9px] py-1 text-right text-ui-text-secondary">
                    {item?.volume ?? "0"} ({volumeUnit})
                  </td>
                </tr>
              ))}

              {parcels.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-2.5 py-4 text-center font-manrope text-[10.5px] font-medium text-ui-text-muted"
                  >
                    {t("invoice.noPackagesOnThisShipment")}
                  </td>
                </tr>
              )}

              {parcels.length > 0 && (
                <tr className="bg-ui-surface-sunken font-bold">
                  <td className="px-[9px] py-1" />
                  <td className="px-[9px] py-1">{t("invoice.total")}</td>
                  <td className="px-[9px] py-1" />
                  <td className="px-[9px] py-1" />
                  {hasCarrierTracking && <td className="px-[9px] py-1" />}
                  {perPackageInsurance && <td className="px-[9px] py-1" />}
                  <td className="px-[9px] py-1" />
                  <td className="px-[9px] py-1 text-right">
                    {data?.total_weight ?? "0"} ({weightUnit})
                  </td>
                  <td className="px-[9px] py-1 text-right">
                    {data?.volume ?? "0"} ({volumeUnit})
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

export default PackagesPanel;
